/**
 * MqttProvider - React Context for MQTT real-time event transport.
 *
 * Provides the MQTT connection manager and client to the component tree.
 * Components access the MQTT client via MqttConsumer (render prop pattern)
 * or useMqttContext (hooks, for functional components).
 *
 * The MqttConsumer render prop passes the MQTT client as the child function
 * argument: {(client) => <Component mqttClient={client} />}
 *
 * The client may be null during initial connection or if credentials
 * are not available.
 */

import React, { Component, createContext } from 'react';
import { createMqttConnectionManager, ConnectionState } from './mqtt-connection-manager';
import { getSessionUukey } from 'helpers/cookies';

// Create the MQTT context
const MqttContext = createContext({
  client: null,
  connectionState: ConnectionState.DISCONNECTED,
  subscribe: () => {},
  unsubscribe: () => {},
  credentials: null,
});

/**
 * MqttProvider wraps the app and manages the MQTT connection lifecycle.
 *
 * Props:
 *   - children: React children
 *
 * Automatically connects on mount and disconnects on unmount.
 */
class MqttProvider extends Component {
  constructor(props) {
    super(props);

    this.manager = createMqttConnectionManager();

    this.state = {
      client: null,
      connectionState: ConnectionState.DISCONNECTED,
      credentials: null,
    };

    this._unsubscribeState = null;
  }

  componentDidMount() {
    // Listen for state changes from the connection manager
    this._unsubscribeState = this.manager.onStateChange((newState) => {
      this.setState({
        connectionState: newState,
        client: this.manager.getClient(),
        credentials: this.manager.getCredentials(),
      });
    });

    // Only connect if the user is authenticated (has a session cookie).
    // Without this guard the 401 response purges cookies and triggers a
    // re-render loop that crashes the login screen.
    if (getSessionUukey()) {
      this.manager.connect();
    }
  }

  componentWillUnmount() {
    // Clean up state listener
    if (this._unsubscribeState) {
      this._unsubscribeState();
    }

    // Disconnect MQTT
    this.manager.disconnect();
  }

  render() {
    const { client, connectionState, credentials } = this.state;
    const contextValue = {
      client,
      connectionState,
      credentials,
      subscribe: this.manager.subscribe,
      unsubscribe: this.manager.unsubscribe,
    };

    return (
      <MqttContext.Provider value={contextValue}>
        {this.props.children}
      </MqttContext.Provider>
    );
  }
}

/**
 * MqttConsumer - Render prop component for MQTT client access.
 *
 * Usage:
 *   <MqttConsumer>
 *     {(client) => <MyComponent mqttClient={client} />}
 *   </MqttConsumer>
 *
 * The child function receives the MQTT client (or null if not connected).
 */
function MqttConsumer({ children }) {
  return (
    <MqttContext.Consumer>
      {({ client }) => children(client)}
    </MqttContext.Consumer>
  );
}

export { MqttProvider, MqttConsumer, MqttContext, ConnectionState };
export default MqttProvider;
